'use client';

import { useRef } from 'react';

import { css } from '@flipster/styled-system/css';
import { countDecimalPlaces } from '@flipster/utils/num';

const barHeight = 4;
const markSize = 10;
const handleSize = 18;

interface SliderProps {
  min: number;
  max: number;
  step: number;
  marks: number[];
  value: number;
  onChange: (value: number) => void;
  getLabel?: (value: number) => string;
}
export const Slider = ({ min, step, marks, value, onChange, getLabel = String }: SliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  function getValueFromPointer(pointerPosition: number) {
    const rect = sliderRef.current!.getBoundingClientRect();
    const input: [number, number] = [0, rect.width];
    const value = markWeightedScale(input, marks);
    return value(pointerPosition - rect.left);
  }
  function updateValue(value: number) {
    const snapToStep = roundValue(Math.round((value - min) / step) * step + min, countDecimalPlaces(step));
    onChange(snapToStep);
  }
  const percent = getMarkWeightedPercent(marks)(value);

  return (
    <div className={css({ w: '100%', px: markSize / 2, h: markSize + handleSize * 2 })}>
      <div
        ref={sliderRef}
        className={css({
          pos: 'relative',
          w: '100%',
          py: (handleSize - barHeight) / 2,
        })}
        onPointerDown={(event) => {
          const target = event.target as HTMLElement;
          target.setPointerCapture(event.pointerId);
          event.preventDefault();
          const value = getValueFromPointer(event.clientX);
          updateValue(value);
        }}
        onPointerMove={(event) => {
          const target = event.target as HTMLElement;
          if (!target.hasPointerCapture(event.pointerId)) return;
          const value = getValueFromPointer(event.clientX);
          updateValue(value);
        }}
        onPointerUp={(event) => {
          const target = event.target as HTMLElement;
          if (!target.hasPointerCapture(event.pointerId)) return;
          target.releasePointerCapture(event.pointerId);
          const value = getValueFromPointer(event.clientX);
          updateValue(value);
        }}
      >
        <span
          className={css({
            position: 'absolute',
            h: barHeight,
            touchAction: 'none',
            bgColor: 'content.accent.info',
            rounded: barHeight,
          })}
          style={{ width: `${percent}%` }}
        />
        <div
          className={css({
            h: barHeight,
            touchAction: 'none',
            bgColor: 'border.default.level-3',
            rounded: barHeight,
          })}
        />
        <div>
          {marks.map((_, index) => {
            const markPercent = (index / (marks.length - 1)) * 100;
            const markOn = markPercent <= percent;
            return (
              <div
                key={index}
                className={css(
                  {
                    position: 'absolute',
                    top: '50%',
                    w: markSize,
                    h: markSize,
                    rounded: markSize,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    transform: 'translate(-50%, -50%)',
                    bgColor: 'background.default.level-4',
                    borderColor: 'border.default.level-3',
                  },
                  markOn && {
                    bgColor: '{colors.blue.300}',
                    borderColor: 'content.accent.info',
                  },
                )}
                style={{ left: `${(index / (marks.length - 1)) * 100}%` }}
              />
            );
          })}
        </div>
        <span
          className={css({
            position: 'absolute',
            w: handleSize,
            h: handleSize,
            cursor: 'grab',
            bgColor: 'content.accent.info',
            rounded: handleSize,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          })}
          style={{ left: `${percent}%` }}
        />
      </div>
      <div className={css({ w: '100%', pos: 'relative', mt: 6 })}>
        {marks.map((mark, index) => (
          <p
            key={index}
            className={css({
              position: 'absolute',
              top: 0,
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-2',
              transform: 'translateX(-50%)',
              cursor: 'pointer',
              _first: { transform: 'none', left: `${-markSize / 2}px!` },
              _last: { transform: 'none', left: 'initial!', right: `${-markSize / 2}px!` },
            })}
            style={{ left: `${(index / (marks.length - 1)) * 100}%` }}
            onClick={() => onChange(mark)}
          >
            {getLabel(mark)}
          </p>
        ))}
      </div>
    </div>
  );
};

function markWeightedScale(input: readonly [number, number], marks: number[]) {
  const unit = 1 / (marks.length - 1);
  return (value: number) => {
    const ratio = (value - input[0]) / (input[1] - input[0]);
    const leftMarkIdx = Math.floor(ratio * (marks.length - 1));
    const [left, right] = [marks[leftMarkIdx], marks[leftMarkIdx + 1]];
    const ratioOnMark = (ratio - leftMarkIdx * unit) / unit;
    if (value <= input[0]) return marks[0];
    if (value >= input[1]) return marks[marks.length - 1];
    if (input[0] === input[1] || left === right) return left;
    return left + ratioOnMark * (right - left);
  };
}
function getMarkWeightedPercent(marks: number[]) {
  const unit = 1 / (marks.length - 1);
  return (value: number) => {
    const rightMarkIdx = (() => {
      const idx = marks.findIndex((mark) => mark >= value);
      return idx === -1 ? marks.length - 1 : idx;
    })();
    const [left, right] = [marks[rightMarkIdx - 1], marks[rightMarkIdx]];
    const ratioOnMark = ((value - left) / (right - left)) * unit;
    if (rightMarkIdx == 0) return 0;
    return (unit * (rightMarkIdx - 1) + ratioOnMark) * 100;
  };
}
function roundValue(value: number, decimalCount: number) {
  const rounder = Math.pow(10, decimalCount);
  return Math.round(value * rounder) / rounder;
}
